import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Drop-in replacement for a native <select className="finput"> that lets the
 * user type to filter the options instead of only scrolling a native list.
 * `options` is [{ value, label, sublabel? }], `value`/`onChange` behave like a
 * controlled select (onChange receives the raw value, not an event). An
 * optional `sublabel` renders as a smaller second line under `label` in the
 * dropdown list (not in the closed input) and is also matched by the
 * type-to-filter search.
 */
export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  required = false,
  disabled = false,
  className = '',
  style,
  id,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [listPos, setListPos] = useState(null);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find((o) => String(o.value) === String(value));
  const filtered = query
    ? options.filter((o) => {
        const q = query.toLowerCase();
        return o.label.toLowerCase().includes(q) || (o.sublabel && o.sublabel.toLowerCase().includes(q));
      })
    : options;

  // The options list is portaled to <body> and positioned with `fixed` so it
  // can escape any scrollable/overflow-hidden ancestor (e.g. a modal body) —
  // otherwise it gets clipped instead of floating above everything.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const reposition = () => {
      const rect = rootRef.current?.getBoundingClientRect();
      if (rect) setListPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target) && listRef.current && !listRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const openList = () => {
    if (disabled) return;
    setOpen(true);
    setQuery('');
    setActiveIndex(-1);
  };

  const commit = (opt) => {
    onChange(opt.value);
    setOpen(false);
    setQuery('');
    inputRef.current?.blur();
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openList();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[activeIndex]) commit(filtered[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
      inputRef.current?.blur();
    }
  };

  return (
    <div className="ssel" ref={rootRef}>
      <input
        ref={inputRef}
        id={id}
        type="text"
        className={className}
        style={style}
        disabled={disabled}
        required={required}
        placeholder={selected ? undefined : placeholder}
        value={open ? query : (selected ? selected.label : '')}
        onFocus={openList}
        onClick={openList}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setActiveIndex(0); }}
        onKeyDown={onKeyDown}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        autoComplete="off"
      />
      <i className="fa-solid fa-chevron-down ssel-chev" />
      {open && listPos && createPortal(
        <div
          ref={listRef}
          className="ssel-list ssel-list-portal"
          role="listbox"
          style={{ top: listPos.top, left: listPos.left, width: listPos.width }}
        >
          {filtered.length === 0 && <div className="ssel-empty">No matches</div>}
          {filtered.map((opt, i) => (
            <div
              key={opt.value}
              role="option"
              aria-selected={String(opt.value) === String(value)}
              className={`ssel-opt ${i === activeIndex ? 'active' : ''} ${String(opt.value) === String(value) ? 'sel' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); commit(opt); }}
              onMouseEnter={() => setActiveIndex(i)}
            >
              <div className="ssel-opt-label">{opt.label}</div>
              {opt.sublabel && <div className="ssel-opt-sub">{opt.sublabel}</div>}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}
